import type { DataSequenceUnit } from "building-types/dataSequences/dataSequenceUnitTypes";
import type {
  KeyAttribute,
  KeyAttributeOptionsArgs,
  KeyAttributeOptions,
  KeyMeasure,
  KeyMeasureOptionsArgs,
  KeyMeasureOptions,
  EntityKeyAttributes,
  BuildingOptionKeyAttributes,
} from "building-types/dataSequences/measures";
import { AggregationMethod } from "building-types/dataSequences/measures";
import { dataSequenceUnitConversionFunctions } from "building-types/dataSequences/scheduleSettingsTable";
import type { SystemDataSequenceEntityType } from "building-types/dataSequences/systemDataSequenceTypes";
import { camelCaseToTitleCase } from "common/utils/strings";

export function keyAttributeNew(value: string, unit?: DataSequenceUnit): KeyAttribute {
  return { value, unit };
}

export function keyAttributeOptionsNew(options: KeyAttributeOptionsArgs): KeyAttributeOptions {
  return {
    definesEntity: true,
    ...options,
  };
}
// Round all measures to single decimal
const roundMeasure = (n: number) => Math.round(n * 10) / 10;

export function keyMeasureNew(
  value: number,
  unit: DataSequenceUnit = "QUANTITY",
  convertFromUnit?: DataSequenceUnit,
): KeyMeasure {
  const convert = convertFromUnit ? dataSequenceUnitConversionFunctions[convertFromUnit]?.[unit] : null;
  const baseValue = roundMeasure(typeof convert !== "function" ? value : convert(value));
  return {
    value: baseValue,
    baseValue,
    unit,
  };
}
export function keyMeasureOptionsNew(options: KeyMeasureOptionsArgs): KeyMeasureOptions {
  return {
    isAggregated: true,
    aggregationMethod: AggregationMethod.SUM,
    ...options,
    unit: options.unit ?? "QUANTITY",
  };
}

export const DEFAULT_KEY_ATTRIBUTE_OPTIONS = keyAttributeOptionsNew({});
export const DEFAULT_KEY_MEASURE_OPTIONS = keyMeasureOptionsNew({});

const BUILDING_OPTION_KEY_ATTRIBUTES_TO_LABELS: {
  [key in keyof BuildingOptionKeyAttributes]: string;
} = {
  optionKey: "OPTION ID",
  optionSet: "OPTION SET NAME",
  description: "OPTION DESCRIPTION",
  optionSetDescription: "OPTION SET DESCRIPTION",
  category: "CATEGORY",
  salesCategory: "SALES CATEGORY",
  builderOptionSetCode: "BUILDER OPTION SET ID",
  optionCode: "BUILDER OPTION ID",
  builderTemplateCode: "BUILDER TEMPLATE CODE",
  name: "OPTION VALUE",
  entityName: "ENTITY NAME",
  price: "OPTION PRICE",
} as const;

export const BUILTIN_DATA_SEQUENCE_ENTITY_TYPE_TO_ATTRIBUTE_DISPLAY_NAME: {
  [type in SystemDataSequenceEntityType]?: {
    [key in keyof EntityKeyAttributes]: string;
  };
} = {
  BUILDING_OPTION: BUILDING_OPTION_KEY_ATTRIBUTES_TO_LABELS,
} as const;

export function formatDataSequenceAttributeDisplayName(type: SystemDataSequenceEntityType, attribute: string): string {
  const labels = BUILTIN_DATA_SEQUENCE_ENTITY_TYPE_TO_ATTRIBUTE_DISPLAY_NAME[type];

  if (labels && attribute in labels) {
    return labels[attribute];
  }

  return camelCaseToTitleCase(attribute);
}

export const BUILTIN_DATA_SEQUENCE_ENTITY_TYPES_TO_OMIT_MEASURES: Partial<Record<SystemDataSequenceEntityType, true>> =
  {
    BUILDING_OPTION: true,
  };

export const BUILTIN_DATA_SEQUENCE_ENTITY_TYPE_INCLUDES_SYMBOL_COLUMN: Partial<
  Record<SystemDataSequenceEntityType, true>
> = {
  ELECTRICAL: true,
};
